import { useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getAllReportCallouts } from "shared/api/function_data";
import { queryKeys } from "shared/lib/queryClient";

/**
 * Layer 2 prefetch — warms the React Query cache for report callouts.
 *
 * Why callouts and NOT PDFs:
 *   - Callouts are tiny text and fetched in ONE batch call (get_all_report_callouts),
 *     so seeding them means a report's callout strip renders instantly with no
 *     extra per-report request.
 *   - PDFs are large binaries. Pre-downloading every report's PDF on page load
 *     would INCREASE bandwidth/API cost for reports the user may never open, which
 *     is the opposite of "optimized". So PDFs stay lazy (fetched + cached on first
 *     open by ReportPage).
 *
 * The seeded cache uses the exact same queryKeys.reportCallout(...) that ReportPage
 * reads, so an upload/callout save still invalidates it correctly (anti-stale holds).
 * We seed with the parsed string ReportPage expects.
 */
function getReportKeys(sidebarOptions) {
  if (!sidebarOptions) return [];
  const keys = [];
  for (const section of Object.values(sidebarOptions)) {
    for (const item of section.items || []) {
      if (item.type === "report") keys.push(item.key);
    }
  }
  return keys;
}

export function usePrefetchReports(functionId, year, week, functionSidebar) {
  const queryClient = useQueryClient();

  const reportKeys = useMemo(
    () => getReportKeys(functionSidebar),
    [functionSidebar]
  );

  useEffect(() => {
    const enabled =
      !!functionId &&
      week !== "N/A" &&
      year !== "N/A" &&
      reportKeys.length > 0;
    if (!enabled) return;

    let cancelled = false;

    (async () => {
      try {
        const results = await getAllReportCallouts(functionId, reportKeys, week, year);
        if (cancelled || !results) return;

        for (const key of reportKeys) {
          const entry = results[key];
          const calloutKey = queryKeys.reportCallout(functionId, key, week, year);

          // Only seed if there is no fresher cached value already (don't clobber
          // something the user just refetched/invalidated).
          const existing = queryClient.getQueryState(calloutKey);
          if (existing && existing.data !== undefined) continue;

          if (entry && entry.status === "success" && entry.callout) {
            queryClient.setQueryData(calloutKey, { text: entry.callout });
          }
        }
      } catch {
        // Prefetch is best-effort. If it fails, ReportPage falls back to its own
        // per-report fetch — no user-visible error.
      }
    })();

    return () => {
      cancelled = true;
    };
    // reportKeys.join covers list changes without an unstable array dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [functionId, year, week, reportKeys.join(","), queryClient]);
}
